import React, { useRef, useEffect, useState } from 'react';
import DxfParser from 'dxf-parser';
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Rect,
  Circle,
  Transformer,
} from 'react-konva';
import { DxfViewer } from 'dxf-viewer';
import * as THREE from 'three';

/**
 * React component with two modes:
 * - Pan mode: Drag to pan & wheel to zoom DXF + annotations together
 * - Annotate mode: Manipulate Konva shapes (drag/resize) atop DXF
 */
export default function DxfUploaderWithKonva() {
  const wrapperRef = useRef(null);
  const dxfContainerRef = useRef(null);
  const stageRef = useRef(null);
  const shapeLayerRef = useRef(null);
  const trRef = useRef(null);

  const [viewer, setViewer] = useState(null);
  const [canvasElem, setCanvasElem] = useState(null);
  const [layers, setLayers] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [annotateMode, setAnnotateMode] = useState(false);

  // Initialize DxfViewer and grab its canvas
  useEffect(() => {
    if (!dxfContainerRef.current || viewer) return;
    const options = { clearColor: new THREE.Color('#ffffff'), autoResize: true, enableSpinner: true };
    const v = new DxfViewer(dxfContainerRef.current, options);
    setViewer(v);
    setTimeout(() => {
      const cvs = dxfContainerRef.current.querySelector('canvas');
      setCanvasElem(cvs);
    }, 0);
  }, [viewer]);

  // Resize listener
  useEffect(() => {
    const update = () => {
      if (dxfContainerRef.current) {
        setStageSize({ width: dxfContainerRef.current.clientWidth, height: dxfContainerRef.current.clientHeight });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // DXF file upload
  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file || !viewer) return;
    const text = await file.text();
    let parsed;
    try { parsed = new DxfParser().parseSync(text); } catch (err) { console.error(err); return; }
    setLayers(Object.entries(parsed.tables.layer.layers).map(([name, rec]) => ({ name, visible: rec.visible })));
    const url = URL.createObjectURL(file);
    await viewer.Load({ modelId: 'uploaded-dxf', name: file.name, url });
    URL.revokeObjectURL(url);
  };

  // Toggle DXF layer
  const toggleLayer = name => {
    if (viewer) viewer.setLayerVisibility(name, !layers.find(l => l.name === name).visible);
    setLayers(layers.map(l => (l.name === name ? { ...l, visible: !l.visible } : l)));
  };

  // Add annotations
  const addRectangle = () => {
    const id = `ann_${annotations.length + 1}`;
    setAnnotations([...annotations, { id, type: 'rect', x: 50, y: 50, width: 100, height: 80, fill: 'rgba(0,0,255,0.3)', stroke: 'blue', strokeWidth: 2 }]);
    setSelectedId(id);
  };
  const addCircle = () => {
    const id = `ann_${annotations.length + 1}`;
    setAnnotations([...annotations, { id, type: 'circle', x: 200, y: 200, radius: 40, fill: 'rgba(255,0,0,0.3)', stroke: 'red', strokeWidth: 2 }]);
    setSelectedId(id);
  };

  // Select annotation
  const selectAnnotation = (e, id) => {
    e.cancelBubble = true;
    setSelectedId(id);
  };

  // Transform end for annotations
  const onTransformEnd = (e, ann) => {
    const node = e.target;
    setAnnotations(annotations.map(a => {
      if (a.id !== ann.id) return a;
      if (a.type === 'rect') {
        const sx = node.scaleX(), sy = node.scaleY();
        node.scaleX(1); node.scaleY(1);
        return { ...a, x: node.x(), y: node.y(), width: a.width * sx, height: a.height * sy };
      }
      const s = node.scaleX(); node.scaleX(1); node.scaleY(1);
      return { ...a, x: node.x(), y: node.y(), radius: a.radius * s };
    }));
  };

  // Sync Transformer
  useEffect(() => {
    if (!shapeLayerRef.current || !trRef.current) return;
    const layer = shapeLayerRef.current;
    const selNode = selectedId ? layer.findOne(`#${selectedId}`) : null;
    trRef.current.nodes(selNode ? [selNode] : []);
    trRef.current.getLayer().batchDraw();
  }, [selectedId, annotations]);

  // Pan/Zoom via Konva stage
  useEffect(() => {
    const stage = stageRef.current.getStage();
    // Enable or disable dragging based on mode
    stage.draggable(!annotateMode);

    // Function to sync transform to DXF container
    const applyTransform = () => {
      const x = stage.x();
      const y = stage.y();
      const scale = stage.scaleX();
      if (dxfContainerRef.current) {
        dxfContainerRef.current.style.transform =
          `translate(${x}px, ${y}px) scale(${scale})`;
      }
    };

    // Apply initial transform
    applyTransform();

    // On drag, move both layers
    stage.on('dragmove', applyTransform);

    // On wheel, zoom around pointer and sync transform
    const onWheel = (e) => {
      if (annotateMode) return;
      e.evt.preventDefault();
      const stage = stageRef.current.getStage();
      const oldScale = stage.scaleX();
      const pointer = stage.getPointerPosition();
      const scaleBy = 1.02;
      const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
      const mouseTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale,
      };
      stage.scale({ x: newScale, y: newScale });
      stage.position({
        x: pointer.x - mouseTo.x * newScale,
        y: pointer.y - mouseTo.y * newScale,
      });
      stage.batchDraw();
      applyTransform();
    };

    // ensure DXF container uses same transform-origin
    if (dxfContainerRef.current) {
      dxfContainerRef.current.style.transformOrigin = '0 0';
    }
    
    stage.on('wheel', onWheel);

    return () => {
      stage.off('wheel', onWheel);
      stage.off('dragmove', applyTransform);
    };
  }, [annotateMode]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '600px' }}>
      {/* Toolbar */}
      <div style={{ position: 'relative', zIndex: 3, marginBottom: 8 }}>
        <input type="file" accept=".dxf" onChange={handleFileChange} />
        <button onClick={() => setAnnotateMode(m => !m)} style={{ marginLeft: 8 }}>
          {annotateMode ? 'Switch to Pan Mode' : 'Switch to Annotate Mode'}
        </button>
        <button onClick={addRectangle} disabled={!annotateMode} style={{ marginLeft: 8 }}>Add Rect</button>
        <button onClick={addCircle} disabled={!annotateMode} style={{ marginLeft: 8 }}>Add Circle</button>
      </div>

      {/* DXF Viewer */}
      <div
        ref={dxfContainerRef}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}
      />

      {/* Konva Stage: background=DXF canvas, overlay=annotations */}
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        style={{ position: 'absolute', top: 0, left: 0, zIndex: 2 }}
      >
        {/* Background Layer (DXF Canvas) */}
        <Layer listening={false}>
          {canvasElem && <KonvaImage image={canvasElem} />}
        </Layer>

        {/* Annotation Layer */}
        <Layer ref={shapeLayerRef}>
          {annotations.map(ann =>
            ann.type === 'rect' ? (
              <Rect
                key={ann.id}
                id={ann.id}
                x={ann.x}
                y={ann.y}
                width={ann.width}
                height={ann.height}
                fill={ann.fill}
                stroke={ann.stroke}
                strokeWidth={ann.strokeWidth}
                draggable={annotateMode}
                onMouseDown={e => annotateMode && selectAnnotation(e, ann.id)}
                onTransformEnd={e => annotateMode && onTransformEnd(e, ann)}
              />
            ) : (
              <Circle
                key={ann.id}
                id={ann.id}
                x={ann.x}
                y={ann.y}
                radius={ann.radius}
                fill={ann.fill}
                stroke={ann.stroke}
                strokeWidth={ann.strokeWidth}
                draggable={annotateMode}
                onMouseDown={e => annotateMode && selectAnnotation(e, ann.id)}
                onTransformEnd={e => annotateMode && onTransformEnd(e, ann)}
              />
            )
          )}
          <Transformer ref={trRef} />
        </Layer>
      </Stage>

      {/* Layer List */}
      <div style={{ position: 'absolute', right: 0, top: 40, width: 200, background: '#fff', zIndex: 4 }}>
        <h4>Layers</h4>
        <ul style={{ listStyle: 'none', padding: 8 }}>
          {layers.map(l => (
            <li key={l.name}>
              <label>
                <input type="checkbox" checked={l.visible} onChange={() => toggleLayer(l.name)} /> {l.name}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
